import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

// Interfaces
import { IAcademicLevel } from 'app/interfaces/academic-level.interface';
import { ISeries } from 'app/interfaces/series';
import { IDepartment } from 'app/interfaces/departments';
import { ISpecialization } from 'app/interfaces/specialization';
import { ISchool } from 'app/interfaces/school.interface';

// Services
import { SchoolService } from 'app/services/school/school.service';
import { FileHandler } from 'app/utils/file-handler.utils';
import { CoreConfigService } from '@core/services/config.service';

@Component({
  selector: 'app-online-registration',
  templateUrl: './online-registration.component.html',
  styleUrls: ['./online-registration.component.scss'],
  providers: [MessageService]
})
export class OnlineRegistrationComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Route parameters
  schoolSlug: string = '';
  schoolInfo: ISchool | null = null;
  isLoadingSchool = true;

  // Form
  registrationForm: FormGroup;
  isSubmitting = false;
  currentStep = 1;
  totalSteps = 4;
  get stepsArray(): number[] {
    return Array.from({ length: this.totalSteps }, (_, i) => i + 1);
  }

  // Data from school
  academicLevels: IAcademicLevel[] = [];
  departments: IDepartment[] = [];
  series: ISeries[] = [];
  specializations: ISpecialization[] = [];
  filteredSeries: ISeries[] = [];
  filteredDepartments: IDepartment[] = [];
  classrooms: any[] = []; // For primary/secondary schools

  // File upload
  profilePhoto: File | null = null;
  documents: File[] = [];
  maxFileSize = 5 * 1024 * 1024; // 5MB
  allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif'];
  allowedDocumentTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

  // Options
  genderOptions: any[] = [];

  // Date validation
  maxDate = new Date();

  // Validation messages
  validationMessages: any = {};

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private schoolService: SchoolService,
    private messageService: MessageService,
    private fileHandler: FileHandler,
    private translateService: TranslateService,
    private coreConfigService: CoreConfigService
  ) {
    // Configure the layout
    this.coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        menu: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        customizer: false,
        enableLocalStorage: false,
      },
    };
    this.initForm();
  }

  ngOnInit(): void {
    this.loadValidationMessages();
    this.getSchoolSlugFromRoute();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Gets school slug from route parameters
   */
  private getSchoolSlugFromRoute(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.schoolSlug = params['slug'];
      if (this.schoolSlug) {
        this.loadSchoolData();
      } else {
        // For testing purposes, use a default school slug
        this.schoolSlug = 'test-school';
        this.loadSchoolData();
      }
    });
  }

  /**
   * Loads school data and related information
   */
  private loadSchoolData(): void {
    this.isLoadingSchool = true;

    // Fetch school data by slug
    this.schoolService.getSchoolBySlug(this.schoolSlug)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response?.status >= 200 && response?.status <= 300) {
            this.schoolInfo = response.data;
            this.loadRequiredData();
          } else {
            this.handleSchoolLoadError();
          }
        },
        error: (error) => {
          console.error('Error loading school data:', error);
          this.handleSchoolLoadError();
        }
      });
  }

  /**
   * Handles school loading error
   */
  private handleSchoolLoadError(): void {
    this.isLoadingSchool = false;
    this.schoolInfo = null;
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('ERRORS.GENERAL'),
      detail: this.translateService.instant('ONLINE_REGISTRATION.ERRORS.SCHOOL_NOT_FOUND')
    });
  }

  /**
   * Load validation messages and options from translation service
   */
  private loadValidationMessages(): void {
    this.validationMessages = {
      required: this.translateService.instant('VALIDATION.REQUIRED'),
      email: this.translateService.instant('VALIDATION.EMAIL'),
      minlength: this.translateService.instant('VALIDATION.MIN_LENGTH'),
      maxlength: this.translateService.instant('VALIDATION.MAX_LENGTH'),
      pattern: this.translateService.instant('VALIDATION.PATTERN'),
      fileSize: this.translateService.instant('ONLINE_REGISTRATION.VALIDATION.FILE_SIZE'),
      fileType: this.translateService.instant('ONLINE_REGISTRATION.VALIDATION.FILE_TYPE'),
      phoneNumber: this.translateService.instant('VALIDATION.PHONE'),
      dateOfBirth: this.translateService.instant('ONLINE_REGISTRATION.VALIDATION.DATE_OF_BIRTH')
    };

    this.genderOptions = [
      { label: this.translateService.instant('STUDENTS.MALE'), value: 'male' },
      { label: this.translateService.instant('STUDENTS.FEMALE'), value: 'female' },
      { label: this.translateService.instant('ONLINE_REGISTRATION.OTHER'), value: 'other' }
    ];
  }

  /**
   * Initializes the registration form
   */
  private initForm(): void {
    this.registrationForm = this.fb.group({
      // Personal Information
      personalInfo: this.fb.group({
        first_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
        last_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
        email: ['', [Validators.required, Validators.email]],
        phone_number: ['', [Validators.required, Validators.pattern(/^\+?[\d\s\-\(\)]+$/)]],
        date_of_birth: ['', [Validators.required, this.dateOfBirthValidator.bind(this)]],
        gender: ['', Validators.required],
        address: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(200)]],
        place_of_birth: ['', [Validators.minLength(2), Validators.maxLength(100)]]
      }),

      // Family Information
      familyInfo: this.fb.group({
        fathers_name: ['', [Validators.minLength(2), Validators.maxLength(100)]],
        fathers_phone: ['', [Validators.pattern(/^\+?[\d\s\-\(\)]+$/)]],
        fathers_occupation: ['', [Validators.minLength(2), Validators.maxLength(100)]],
        mothers_name: ['', [Validators.minLength(2), Validators.maxLength(100)]],
        mothers_phone: ['', [Validators.pattern(/^\+?[\d\s\-\(\)]+$/)]],
        mothers_occupation: ['', [Validators.minLength(2), Validators.maxLength(100)]],
        guardian_name: ['', [Validators.minLength(2), Validators.maxLength(100)]],
        guardian_contact_phone: ['', [Validators.pattern(/^\+?[\d\s\-\(\)]+$/)]],
        guardian_address: ['', [Validators.minLength(10), Validators.maxLength(200)]],
        guardian_relationship: ['', [Validators.minLength(2), Validators.maxLength(50)]],
        guardian_occupation: ['', [Validators.minLength(2), Validators.maxLength(100)]]
      }),

      // Academic Information
      academicInfo: this.fb.group({
        academic_level: [''],
        specialization: [''],
        department: [''],
        series: [''],
        classroom: [''], // For primary/secondary schools
        special_needs: [''],
        medical_conditions: [''],
        emergency_contact: ['', [Validators.required, Validators.pattern(/^\+?[\d\s\-\(\)]+$/)]],
        emergency_relationship: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]]
      }),

      // Additional Information
      additionalInfo: this.fb.group({
        hobbies: ['', [Validators.minLength(5), Validators.maxLength(200)]],
        languages: ['', [Validators.minLength(2), Validators.maxLength(100)]],
        achievements: ['', [Validators.minLength(5), Validators.maxLength(500)]],
        goals: ['', [Validators.minLength(10), Validators.maxLength(500)]],
        notes: ['', [Validators.maxLength(1000)]]
      })
    });

    this.setupFormSubscriptions();
  }

  /**
   * Sets up dynamic validators based on school type
   */
  private setupDynamicValidators(): void {
    if (!this.schoolInfo) return;

    const academicInfo = this.registrationForm.get('academicInfo') as FormGroup;

    switch (this.schoolInfo.type) {
      case 'UNIVERSITY':
      case 'HIGHER':
        // University/higher education requires specialization, department, series
        academicInfo.get('specialization')?.setValidators([Validators.required]);
        academicInfo.get('department')?.setValidators([Validators.required]);
        academicInfo.get('series')?.setValidators([Validators.required]);
        academicInfo.get('classroom')?.clearValidators();
        
        // Enable/disable controls based on school type
        academicInfo.get('specialization')?.enable();
        academicInfo.get('department')?.enable();
        academicInfo.get('series')?.enable();
        academicInfo.get('classroom')?.disable();
        break;
      case 'PRIMARY':
      case 'SECONDARY':
        // Primary/secondary schools require classroom
        academicInfo.get('classroom')?.setValidators([Validators.required]);
        academicInfo.get('specialization')?.clearValidators();
        academicInfo.get('department')?.clearValidators();
        academicInfo.get('series')?.clearValidators();
        
        // Enable/disable controls based on school type
        academicInfo.get('classroom')?.enable();
        academicInfo.get('specialization')?.disable();
        academicInfo.get('department')?.disable();
        academicInfo.get('series')?.disable();
        break;
    }

    // Update validation
    academicInfo.get('specialization')?.updateValueAndValidity();
    academicInfo.get('department')?.updateValueAndValidity();
    academicInfo.get('series')?.updateValueAndValidity();
    academicInfo.get('classroom')?.updateValueAndValidity();
  }

  /**
   * Sets up form subscriptions for dynamic validation
   */
  private setupFormSubscriptions(): void {
    // Filter series when department changes
    this.registrationForm.get('academicInfo.department')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(departmentId => {
        this.filterSeriesByDepartment(departmentId);
      });

    // Filter departments when specialization changes
    this.registrationForm.get('academicInfo.specialization')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(specializationId => {
        this.filterDepartmentBySpecialization(specializationId);
      });
  }

  /**
   * Loads required data for the form
   */
  private loadRequiredData(): void {
    // For testing purposes, load mock data if school slug is test-school
    if (this.schoolSlug === 'test-school') {
      this.loadMockData();
      this.isLoadingSchool = false;
      return;
    }

    // Extract data from school object based on school type
    if (this.schoolInfo) {
      this.extractSchoolData();
    }
    this.isLoadingSchool = false;
  }

  /**
   * Extracts data from school object based on school type
   */
  private extractSchoolData(): void {
    if (!this.schoolInfo) return;

    // Handle different school types
    switch (this.schoolInfo.type) {
      case 'UNIVERSITY':
      case 'HIGHER':
        this.extractUniversityData();
        break;
      case 'PRIMARY':
      case 'SECONDARY':
        this.extractPrimarySecondaryData();
        break;
      default:
        console.warn('Unknown school type:', this.schoolInfo.type);
        break;
    }

    // Setup dynamic validators based on school type
    this.setupDynamicValidators();
  }

  /**
   * Extracts data for university/higher education schools
   */
  private extractUniversityData(): void {
    if (!this.schoolInfo) return;

    // Extract specializations from school
    this.specializations = this.schoolInfo.specializations || [];
    
    // Extract departments from specializations
    this.departments = [];
    this.series = [];
    
    this.specializations.forEach(specialization => {
      if (specialization.departments) {
        this.departments.push(...specialization.departments);
        
        // Extract series from departments
        specialization.departments.forEach(department => {
          if (department.series) {
            this.series.push(...department.series);
          }
        });
      }
    });

    // Initialize filtered arrays
    this.filteredDepartments = this.departments;
    this.filteredSeries = this.series;
  }

  /**
   * Extracts data for primary/secondary schools
   */
  private extractPrimarySecondaryData(): void {
    if (!this.schoolInfo) return;

    // For primary/secondary schools, we might have classrooms
    // This would need to be implemented based on your classroom interface
    // For now, we'll use a simplified approach
    this.classrooms = []; // This would be populated from school.classrooms if available
  }

  /**
   * Loads mock data for testing purposes
   */
  private loadMockData(): void {
    // Mock academic levels
    this.academicLevels = [
      {
        id: 1,
        name: 'Bachelor\'s Degree',
        description: 'Undergraduate degree program',
        code: 'BSC',
        learners: [],
        objectives: [],
        courses: [],
        assignments: [],
        activities: [],
        exams: [],
        instructors: [],
        classrooms: []
      },
      {
        id: 2,
        name: 'Master\'s Degree',
        description: 'Graduate degree program',
        code: 'MSC',
        learners: [],
        objectives: [],
        courses: [],
        assignments: [],
        activities: [],
        exams: [],
        instructors: [],
        classrooms: []
      },
      {
        id: 3,
        name: 'Doctorate',
        description: 'Doctoral degree program',
        code: 'PHD',
        learners: [],
        objectives: [],
        courses: [],
        assignments: [],
        activities: [],
        exams: [],
        instructors: [],
        classrooms: []
      }
    ];

    // Mock specializations
    this.specializations = [
      {
        id: 1,
        name: 'Computer Science',
        description: 'Computer Science and Technology',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 2,
        name: 'Business Administration',
        description: 'Business and Management Studies',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 3,
        name: 'Engineering',
        description: 'Engineering and Technology',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 4,
        name: 'Medicine',
        description: 'Medical and Health Sciences',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ];

    // Mock departments
    this.departments = [
      {
        id: 1,
        name: 'Software Engineering',
        description: 'Software Development and Engineering',
        specialization: this.specializations[0],
        created_at: new Date(),
        updated_at: new Date()
      },
      {
        id: 2,
        name: 'Data Science',
        description: 'Data Analysis and Machine Learning',
        specialization: this.specializations[0],
        created_at: new Date(),
        updated_at: new Date()
      },
      {
        id: 3,
        name: 'Finance',
        description: 'Financial Management and Analysis',
        specialization: this.specializations[1],
        created_at: new Date(),
        updated_at: new Date()
      },
      {
        id: 4,
        name: 'Marketing',
        description: 'Marketing and Sales Management',
        specialization: this.specializations[1],
        created_at: new Date(),
        updated_at: new Date()
      },
      {
        id: 5,
        name: 'Mechanical Engineering',
        description: 'Mechanical Systems and Design',
        specialization: this.specializations[2],
        created_at: new Date(),
        updated_at: new Date()
      },
      {
        id: 6,
        name: 'Civil Engineering',
        description: 'Infrastructure and Construction',
        specialization: this.specializations[2],
        created_at: new Date(),
        updated_at: new Date()
      }
    ];

    // Mock series
    this.series = [
      {
        id: 1,
        name: 'Web Development',
        description: 'Full-stack web development',
        department: this.departments[0],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 2,
        name: 'Mobile Development',
        description: 'iOS and Android development',
        department: this.departments[0],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 3,
        name: 'Machine Learning',
        description: 'AI and ML algorithms',
        department: this.departments[1],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 4,
        name: 'Investment Banking',
        description: 'Financial markets and investments',
        department: this.departments[2],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 5,
        name: 'Digital Marketing',
        description: 'Online marketing strategies',
        department: this.departments[3],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 6,
        name: 'Automotive Design',
        description: 'Vehicle design and engineering',
        department: this.departments[4],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ];

    // Initialize filtered arrays
    this.filteredDepartments = this.departments;
    this.filteredSeries = this.series;
  }


  /**
   * Filters series by department
   */
  private filterSeriesByDepartment(departmentId: number): void {
    if (!departmentId) {
      this.filteredSeries = this.series;
      return;
    }

    this.filteredSeries = this.series.filter(series =>
      series.department?.id === departmentId
    );

    // Reset series selection if current selection is not valid
    const currentSeries = this.registrationForm.get('academicInfo.series')?.value;
    if (currentSeries && !this.filteredSeries.find(c => c.id === currentSeries)) {
      this.registrationForm.get('academicInfo.series')?.setValue('');
    }
  }

  /**
   * Filters departments by specialization
   */
  private filterDepartmentBySpecialization(specializationId: number): void {
    if (!specializationId) {
      this.filteredDepartments = this.departments;
      return;
    }

    this.filteredDepartments = this.departments.filter(department =>
      department.specialization?.id === specializationId
    );

    // Reset department selection if current selection is not valid
    const currentDepartment = this.registrationForm.get('academicInfo.department')?.value;
    if (currentDepartment && !this.filteredDepartments.find(c => c.id === currentDepartment)) {
      this.registrationForm.get('academicInfo.department')?.setValue('');
    }
  }

  /**
   * Custom validator for date of birth
   */
  private dateOfBirthValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const birthDate = new Date(control.value);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    if (age < 5) {
      return { dateOfBirth: true };
    }

    return null;
  }

  /**
   * Handles profile photo selection
   */
  onProfilePhotoSelect(event: any): void {
    const file = event.files[0];
    if (this.validateFile(file, 'image')) {
      this.profilePhoto = file;
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('COMMON.SUCCESS'),
        detail: this.translateService.instant('ONLINE_REGISTRATION.MESSAGES.PROFILE_PHOTO_SUCCESS')
      });
    }
  }

  /**
   * Handles document selection
   */
  onDocumentSelect(event: any): void {
    const files = event.files;
    const validFiles: File[] = [];

    for (const file of files) {
      if (this.validateFile(file, 'document')) {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      this.documents.push(...validFiles);
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('COMMON.SUCCESS'),
        detail: this.translateService.instant('ONLINE_REGISTRATION.MESSAGES.DOCUMENTS_SUCCESS', { count: validFiles.length })
      });
    }
  }

  /**
   * Validates uploaded files
   */
  private validateFile(file: File, type: 'image' | 'document'): boolean {
    // Check file size
    if (file.size > this.maxFileSize) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('ERRORS.GENERAL'),
        detail: this.translateService.instant('ONLINE_REGISTRATION.VALIDATION.FILE_SIZE')
      });
      return false;
    }

    // Check file type
    if (type === 'image' && !this.allowedImageTypes.includes(file.type)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('ERRORS.GENERAL'),
        detail: this.translateService.instant('ONLINE_REGISTRATION.VALIDATION.FILE_TYPE')
      });
      return false;
    }

    if (type === 'document' && !this.allowedDocumentTypes.includes(file.type)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('ERRORS.GENERAL'),
        detail: this.translateService.instant('ONLINE_REGISTRATION.VALIDATION.FILE_TYPE')
      });
      return false;
    }

    return true;
  }

  /**
   * Removes a document from the list
   */
  removeDocument(index: number): void {
    this.documents.splice(index, 1);
  }

  /**
   * Removes profile photo
   */
  removeProfilePhoto(): void {
    this.profilePhoto = null;
  }

  /**
   * Navigates to the next step
   */
  nextStep(): void {
    if (this.validateCurrentStep()) {
      this.currentStep++;
    }
  }

  /**
   * Navigates to the previous step
   */
  previousStep(): void {
    this.currentStep--;
  }

  /**
   * Validates the current step
   */
  private validateCurrentStep(): boolean {
    let formGroup: FormGroup;

    switch (this.currentStep) {
      case 1:
        formGroup = this.registrationForm.get('personalInfo') as FormGroup;
        break;
      case 2:
        formGroup = this.registrationForm.get('familyInfo') as FormGroup;
        break;
      case 3:
        formGroup = this.registrationForm.get('academicInfo') as FormGroup;
        break;
      case 4:
        formGroup = this.registrationForm.get('additionalInfo') as FormGroup;
        break;
      default:
        return true;
    }

    if (formGroup.invalid) {
      this.markFormGroupTouched(formGroup);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('ONLINE_REGISTRATION.VALIDATION.VALIDATION_ERROR'),
        detail: this.translateService.instant('ONLINE_REGISTRATION.VALIDATION.FILL_REQUIRED_FIELDS')
      });
      return false;
    }

    return true;
  }

  /**
   * Marks all controls in a form group as touched
   */
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  /**
   * Submits the registration form
   */
  onSubmit(): void {
    if (this.registrationForm.invalid) {
      this.markFormGroupTouched(this.registrationForm);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('ONLINE_REGISTRATION.VALIDATION.VALIDATION_ERROR'),
        detail: this.translateService.instant('ONLINE_REGISTRATION.VALIDATION.FILL_REQUIRED_FIELDS')
      });
      return;
    }

    if (!this.profilePhoto) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('ERRORS.GENERAL'),
        detail: this.translateService.instant('ONLINE_REGISTRATION.VALIDATION.PROFILE_PHOTO_REQUIRED')
      });
      return;
    }

    this.isSubmitting = true;

    // TODO: Implement actual submission logic
    // For now, simulate submission
    setTimeout(() => {
      this.isSubmitting = false;
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('COMMON.SUCCESS'),
        detail: this.translateService.instant('ONLINE_REGISTRATION.MESSAGES.REGISTRATION_SUCCESS')
      });
    }, 2000);
  }

  /**
   * Gets validation message for a field
   */
  getValidationMessage(controlName: string, section: string): string {
    const control = this.registrationForm.get(`${section}.${controlName}`);
    if (!control || !control.errors || !control.touched) return '';

    const error = Object.keys(control.errors)[0];
    let message = this.validationMessages[error] || 'Invalid input';

    if (error === 'minlength') {
      message = message.replace('{0}', control.errors[error].requiredLength);
    } else if (error === 'maxlength') {
      message = message.replace('{0}', control.errors[error].requiredLength);
    }

    return message;
  }

  /**
   * Gets photo preview URL for display
   */
  getPhotoPreview(): string {
    if (!this.profilePhoto) return '';
    return URL.createObjectURL(this.profilePhoto);
  }

  /**
   * Checks if a field is invalid
   */
  isFieldInvalid(controlName: string, section: string): boolean {
    const control = this.registrationForm.get(`${section}.${controlName}`);
    return !!(control && control.invalid && control.touched);
  }

  /**
   * Gets step title
   */
  getStepTitle(step: number): string {
    const titles = [
      this.translateService.instant('ONLINE_REGISTRATION.STEPS.PERSONAL_INFO'),
      this.translateService.instant('ONLINE_REGISTRATION.STEPS.FAMILY_INFO'),
      this.translateService.instant('ONLINE_REGISTRATION.STEPS.ACADEMIC_INFO'),
      this.translateService.instant('ONLINE_REGISTRATION.STEPS.ADDITIONAL_INFO')
    ];
    return titles[step - 1] || '';
  }

  /**
   * Gets step description
   */
  getStepDescription(step: number): string {
    const descriptions = [
      this.translateService.instant('ONLINE_REGISTRATION.STEP_DESCRIPTIONS.PERSONAL_INFO'),
      this.translateService.instant('ONLINE_REGISTRATION.STEP_DESCRIPTIONS.FAMILY_INFO'),
      this.translateService.instant('ONLINE_REGISTRATION.STEP_DESCRIPTIONS.ACADEMIC_INFO'),
      this.translateService.instant('ONLINE_REGISTRATION.STEP_DESCRIPTIONS.ADDITIONAL_INFO')
    ];
    return descriptions[step - 1] || '';
  }

  /**
   * Checks if the school is a university or higher education institution
   */
  isUniversitySchool(): boolean {
    return this.schoolInfo?.type === 'UNIVERSITY' || this.schoolInfo?.type === 'HIGHER';
  }

  /**
   * Checks if the school is a primary or secondary school
   */
  isPrimarySecondarySchool(): boolean {
    return this.schoolInfo?.type === 'PRIMARY' || this.schoolInfo?.type === 'SECONDARY';
  }
}
